// High-level offline API used by the rest of the app.
// Internally delegates to db.ts (IndexedDB) and blobCache.ts (Cache API).
//
// The convention: every function that writes or reads offline state
// returns a meaningful fallback value when IndexedDB is unavailable
// (private-browsing modes, storage disabled), so callers never have
// to branch on availability.

import type { Song, Collection } from '../../types/songbook'
import {
  getOfflineDB,
  type CachedSongRecord,
  type CachedCollectionRecord,
  type AttachmentPrefsRecord,
} from './db'

export type { CachedSongRecord, CachedCollectionRecord, AttachmentPrefsRecord }

export type SongSource = 'own' | 'shared'

// ─── Songs ──────────────────────────────────────────────────────

export async function saveSongOffline(
  song: Song,
  source: SongSource,
  shareToken?: string | null,
  pendingSync?: boolean,
): Promise<void> {
  try {
    const db = await getOfflineDB()
    // Preserve an existing pendingSync flag so that ongoing edits via
    // debouncedSave do not accidentally mark a created-offline song as
    // already-synced.
    const existing = await db.get('songs', song.id)
    const record: CachedSongRecord = {
      id: song.id,
      song,
      cachedAt: Date.now(),
      source,
      shareToken: shareToken ?? existing?.shareToken ?? null,
      pendingSync: pendingSync ?? existing?.pendingSync,
    }
    await db.put('songs', record)
  } catch (err) {
    console.warn('[offline] saveSongOffline failed:', err)
  }
}

export async function clearSongPendingSync(id: string): Promise<void> {
  try {
    const db = await getOfflineDB()
    const existing = await db.get('songs', id)
    if (!existing || !existing.pendingSync) return
    await db.put('songs', { ...existing, pendingSync: false })
  } catch (err) {
    console.warn('[offline] clearSongPendingSync failed:', err)
  }
}

export async function listPendingSongs(): Promise<CachedSongRecord[]> {
  try {
    const db = await getOfflineDB()
    const all = await db.getAll('songs')
    return all.filter((r) => r.pendingSync === true)
  } catch {
    return []
  }
}

export async function loadSongOffline(id: string): Promise<Song | null> {
  try {
    const db = await getOfflineDB()
    const record = await db.get('songs', id)
    return record?.song ?? null
  } catch (err) {
    console.warn('[offline] loadSongOffline failed:', err)
    return null
  }
}

export async function listOfflineSongs(): Promise<CachedSongRecord[]> {
  try {
    const db = await getOfflineDB()
    return await db.getAll('songs')
  } catch (err) {
    console.warn('[offline] listOfflineSongs failed:', err)
    return []
  }
}

export async function removeSongOffline(id: string): Promise<void> {
  try {
    const db = await getOfflineDB()
    await db.delete('songs', id)
    await db.delete('attachmentPrefs', id)
  } catch (err) {
    console.warn('[offline] removeSongOffline failed:', err)
  }
}

// ─── Collections ────────────────────────────────────────────────

export async function saveCollectionsOffline(
  collections: Collection[],
): Promise<void> {
  try {
    const db = await getOfflineDB()
    const tx = db.transaction('collections', 'readwrite')
    const now = Date.now()
    const serverIds = new Set(collections.map((c) => c.id))

    // Reconcile: drop cached collections that no longer exist on the
    // server (e.g. deleted from another device), but never touch
    // offline-created records that are still waiting to sync.
    const existingRecords = await tx.store.getAll()
    await Promise.all(
      existingRecords.map((record) => {
        if (serverIds.has(record.id) || record.pendingSync) return
        return tx.store.delete(record.id)
      }),
    )

    await Promise.all(
      collections.map(async (collection) => {
        // Preserve pendingSync flag for offline-created collections
        // so that a fresh fetchCollections() doesn't accidentally
        // mark them as already-synced.
        const existing = await tx.store.get(collection.id)
        await tx.store.put({
          id: collection.id,
          collection,
          cachedAt: now,
          pendingSync: existing?.pendingSync,
        } satisfies CachedCollectionRecord)
      }),
    )
    await tx.done
  } catch (err) {
    console.warn('[offline] saveCollectionsOffline failed:', err)
  }
}

export async function saveCollectionOfflinePending(
  collection: Collection,
): Promise<void> {
  try {
    const db = await getOfflineDB()
    await db.put('collections', {
      id: collection.id,
      collection,
      cachedAt: Date.now(),
      pendingSync: true,
    })
  } catch (err) {
    console.warn('[offline] saveCollectionOfflinePending failed:', err)
  }
}

export async function clearCollectionPendingSync(id: string): Promise<void> {
  try {
    const db = await getOfflineDB()
    const existing = await db.get('collections', id)
    if (!existing || !existing.pendingSync) return
    await db.put('collections', { ...existing, pendingSync: false })
  } catch (err) {
    console.warn('[offline] clearCollectionPendingSync failed:', err)
  }
}

export async function listPendingCollections(): Promise<CachedCollectionRecord[]> {
  try {
    const db = await getOfflineDB()
    const all = await db.getAll('collections')
    return all.filter((r) => r.pendingSync === true)
  } catch {
    return []
  }
}

export async function removeCollectionOffline(id: string): Promise<void> {
  try {
    const db = await getOfflineDB()
    await db.delete('collections', id)
  } catch (err) {
    console.warn('[offline] removeCollectionOffline failed:', err)
  }
}

export async function loadCollectionsOffline(): Promise<Collection[]> {
  try {
    const db = await getOfflineDB()
    const records = await db.getAll('collections')
    return records.map((r) => r.collection)
  } catch (err) {
    console.warn('[offline] loadCollectionsOffline failed:', err)
    return []
  }
}

export async function saveSongsListOffline(songs: Song[]): Promise<void> {
  try {
    const db = await getOfflineDB()
    const tx = db.transaction('songs', 'readwrite')
    const now = Date.now()
    const serverIds = new Set(songs.map((s) => s.id))

    // Reconcile: drop cached songs that no longer exist on the server
    // (e.g. deleted from another device). Two kinds of records must
    // survive: offline-created songs still waiting to sync, and shared
    // songs (which never appear in the owner's /songs list).
    const existingRecords = await tx.store.getAll()
    await Promise.all(
      existingRecords.map((record) => {
        if (serverIds.has(record.id)) return
        if (record.pendingSync || record.source === 'shared') return
        return tx.store.delete(record.id)
      }),
    )

    await Promise.all(
      songs.map(async (song) => {
        // Never overwrite a record that's waiting to be synced to the cloud
        const existing = await tx.store.get(song.id)
        if (existing?.pendingSync) return
        return tx.store.put({
          id: song.id,
          song,
          cachedAt: now,
          source: 'own' as const,
        } satisfies CachedSongRecord)
      }),
    )
    await tx.done
  } catch (err) {
    console.warn('[offline] saveSongsListOffline failed:', err)
  }
}

export async function loadSongsListOffline(): Promise<Song[]> {
  try {
    const db = await getOfflineDB()
    const records = await db.getAll('songs')
    return records.map((r) => r.song)
  } catch (err) {
    console.warn('[offline] loadSongsListOffline failed:', err)
    return []
  }
}

// ─── Attachment Prefs (per-song "include audio?") ───────────────

export async function setAttachmentPrefs(
  songId: string,
  includeAudio: boolean,
): Promise<void> {
  try {
    const db = await getOfflineDB()
    await db.put('attachmentPrefs', {
      songId,
      includeAudio,
      updatedAt: Date.now(),
    } satisfies AttachmentPrefsRecord)
  } catch (err) {
    console.warn('[offline] setAttachmentPrefs failed:', err)
  }
}

export async function getAttachmentPrefs(
  songId: string,
): Promise<AttachmentPrefsRecord | null> {
  try {
    const db = await getOfflineDB()
    return (await db.get('attachmentPrefs', songId)) ?? null
  } catch (err) {
    console.warn('[offline] getAttachmentPrefs failed:', err)
    return null
  }
}

// ─── Share-token lookups (Phase 7) ──────────────────────────────

/**
 * Returns every cached song that was stored under the given share
 * token. A single token typically yields exactly one song (single
 * song share) or several songs (collection share — every member
 * carries the same collection token).
 *
 * Sorted by createdAt ascending so collection shares render in
 * the same order as the original online list.
 */
export async function loadSongsByShareToken(token: string): Promise<Song[]> {
  if (!token) return []
  try {
    const db = await getOfflineDB()
    const all = await db.getAll('songs')
    return all
      .filter((r) => r.shareToken === token)
      .map((r) => r.song)
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
  } catch (err) {
    console.warn('[offline] loadSongsByShareToken failed:', err)
    return []
  }
}

// ─── Cached collection metadata for share links ─────────────────

interface CachedSharedCollectionMeta {
  name: string
  artist: string
  cachedAt: number
}

const SHARED_COLLECTION_META_PREFIX = 'sharedCollection:'

export async function saveSharedCollectionMeta(
  token: string,
  meta: { name: string; artist: string },
): Promise<void> {
  if (!token) return
  try {
    const db = await getOfflineDB()
    await db.put('meta', {
      key: SHARED_COLLECTION_META_PREFIX + token,
      value: { ...meta, cachedAt: Date.now() } satisfies CachedSharedCollectionMeta,
    })
  } catch (err) {
    console.warn('[offline] saveSharedCollectionMeta failed:', err)
  }
}

export async function loadSharedCollectionMeta(
  token: string,
): Promise<{ name: string; artist: string } | null> {
  if (!token) return null
  try {
    const db = await getOfflineDB()
    const record = await db.get('meta', SHARED_COLLECTION_META_PREFIX + token)
    if (!record) return null
    const meta = record.value as CachedSharedCollectionMeta
    return { name: meta.name, artist: meta.artist }
  } catch (err) {
    console.warn('[offline] loadSharedCollectionMeta failed:', err)
    return null
  }
}
